import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import settingsService from '../services/settingsService';
import { canUseLocalStorage } from '../components/CookieConsent';

const SettingsContext = createContext();

// ─── Keys that must NEVER be stored in frontend state ────────────────────────
const SENSITIVE_KEYS = new Set([
  'razorpayKeySecret',
  'easebuzzKey',
  'easebuzzSalt',
  'easebuzzWebhookSecret',
  'vistaApiUrl',
  'vistaApiKey',
  'vistaApiSecret',
  'brevoApiKey',     // kept here so it's stripped if backend still sends it
  'zeptoApiKey',
  'smsApiKey',
  'allowedIpAddresses',
  'maxLoginAttempts',
  'passwordExpiry',
  'logLevel',
  'enableLogging',
  'enableBackup',
  'backupFrequency',
  'enableIpWhitelist',
]);

const sanitizeSettings = (raw) => {
  if (!raw || typeof raw !== 'object') return {};
  const safe = { ...raw };
  for (const key of SENSITIVE_KEYS) {
    delete safe[key];
  }
  return safe;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchSettings = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      const result = await settingsService.getSettings(forceRefresh);

      if (result.success) {
        setSettings(sanitizeSettings(result.data));
        setLastUpdated(Date.now());

        if (result.cached && process.env.NODE_ENV === 'development') {
          console.warn('Using cached settings — some values may be outdated');
        }
      } else {
        setError(result.error || 'Failed to load settings');
      }
    } catch (err) {
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshSettings = useCallback(() => fetchSettings(true), [fetchSettings]);

  const instantRefresh = useCallback(() => {
    if (canUseLocalStorage()) {
      localStorage.removeItem('mycinemas_settings');
    }
    return fetchSettings(true); // Force refresh
  }, []);

  // ─── Scoped getters ───────────────────────────────────────────────────────

  const getSetting = useCallback((key, defaultValue = null) => {
    if (SENSITIVE_KEYS.has(key)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`getSetting: "${key}" is sensitive and cannot be accessed from the frontend.`);
      }
      return defaultValue;
    }
    return settings?.[key] !== undefined ? settings[key] : defaultValue;
  }, [settings]);

  const getGeneralSettings = useCallback(() => ({
    siteName:        settings?.siteName        || 'MyCinemas',
    siteDescription: settings?.siteDescription || "Kerala's Premier Cinema Experience",
    timezone:        settings?.timezone        || 'Asia/Kolkata',
    currency:        settings?.currency        || 'INR',
    language:        settings?.language        || 'en',
    contactEmail:    settings?.contactEmail    || 'support@mycinemas.com',
    contactPhone:    settings?.contactPhone    || '+91-484-1234567',
    address:         settings?.address         || 'MG Road, Kochi, Kerala 682001',
  }), [settings]);

  const getBookingSettings = useCallback(() => ({
    maxSeatsPerBooking:   settings?.maxSeatsPerBooking   ?? 8,
    bookingTimeout:       settings?.bookingTimeout       ?? 15,
    cancellationDeadline: settings?.cancellationDeadline ?? 2,
    refundProcessingDays: settings?.refundProcessingDays ?? 3,
    enableSeatSelection:  settings?.enableSeatSelection  ?? true,
    enableAdvanceBooking: settings?.enableAdvanceBooking ?? true,
    advanceBookingDays:   settings?.advanceBookingDays   ?? 30,
  }), [settings]);

  const getPaymentSettings = useCallback(() => ({
    paymentGateway:       settings?.paymentGateway      || 'razorpay',
    razorpayKeyId:        settings?.razorpayKeyId       || '',  // public key — safe
    paymentTimeout:       settings?.paymentTimeout      ?? 10,
    enableUPI:            settings?.enableUPI            ?? true,
    enableCards:          settings?.enableCards          ?? true,
    enableNetBanking:     settings?.enableNetBanking     ?? true,
    enableWallet:         settings?.enableWallet         ?? true,
    enableCashOnDelivery: settings?.enableCashOnDelivery ?? false,
    enableConvenienceFee: settings?.enableConvenienceFee ?? false,
    convenienceFeeType:   settings?.convenienceFeeType   || 'fixed',
    convenienceFeeValue:  settings?.convenienceFeeValue  ?? 0,
    // ⚠️ razorpayKeySecret, easebuzzKey/Salt, etc. are never here
  }), [settings]);

  // Zepto sender display info only — API key is stripped in sanitizeSettings
  const getNotificationSettings = useCallback(() => ({
    emailSenderName:  settings?.zeptoSenderName  || 'MyCinemas',
    emailSenderEmail: settings?.zeptoSenderEmail || 'noreply@mycinemas.in',
    whatsappName:     settings?.whatsappBusinessName   || 'MyCinemas',
    whatsappNumber:   settings?.whatsappBusinessNumber || '+918484123456',
    // ⚠️ zeptoApiKey is never here
  }), [settings]);

  // ─── Side effects ─────────────────────────────────────────────────────────

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'admin_settings_updated') {
        instantRefresh();
      }
    };
  
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [instantRefresh]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && canUseLocalStorage()) {
        const lastAdminUpdate = localStorage.getItem('admin_settings_updated');
        const lastUpdate      = localStorage.getItem('last_settings_update');
        if (lastAdminUpdate && (!lastUpdate || parseInt(lastAdminUpdate) > parseInt(lastUpdate))) {
          instantRefresh();
          localStorage.setItem('last_settings_update', Date.now().toString());
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // ─── Context value ────────────────────────────────────────────────────────
  const value = {
    settings, // ✅ ADD THIS
    loading,
    error,
    lastUpdated,
    refreshSettings,
    instantRefresh,
    getSetting,
    getGeneralSettings,
    getBookingSettings,
    getPaymentSettings,
    getNotificationSettings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export default SettingsContext;